import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';

const BTN = {
  padding: '6px 12px', backgroundColor: '#f3f4f6', color: '#374151',
  border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer',
  fontSize: '0.8rem', fontWeight: '500', whiteSpace: 'nowrap',
};

const CATEGORY_IDS = ['valves', 'ubolts', 'gaskets', 'cs', 'ss_duplex', 'hdpe', 'cs_vic', 'cpvc'];

const purchasingLineKey = (line) => line.line_key || `${line.description}||${line.unit || ''}`;

/**
 * Isolated Send-to-Purchasing modal.
 * Owns its own checkbox / preview state so toggling categories or line items
 * never re-renders the (large) MTO grid underneath.
 */
const SendToPurchasingModal = memo(function SendToPurchasingModal({
  isOpen,
  onClose,
  estimateNumber,
  jobNumber,
  selectedRev,
  selectedIds,
  filteredItemIds,
  onSent,
  showToast,
}) {
  const { instance, accounts } = useMsal();
  const [recipients, setRecipients] = useState('');
  const [message, setMessage] = useState('');
  const [selectedOnly, setSelectedOnly] = useState(false);
  const [sending, setSending] = useState(false);
  const [categories, setCategories] = useState(() => new Set(CATEGORY_IDS));
  const [preview, setPreview] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [nextReq, setNextReq] = useState('');
  const [qtyEdits, setQtyEdits] = useState({});
  const [checkedKeys, setCheckedKeys] = useState(() => new Set());
  const [specFile, setSpecFile] = useState(null);
  const [specLink, setSpecLink] = useState('');
  const [header, setHeader] = useState({
    ship_to: '', site_contact: '', contact_no: '',
    date_required: '', spec_class: '', request_type: 'Quote',
  });

  const selectedIdsArr = useMemo(
    () => (selectedIds instanceof Set ? Array.from(selectedIds) : (selectedIds || [])),
    [selectedIds]
  );
  const filteredIdsArr = useMemo(
    () => (Array.isArray(filteredItemIds) ? filteredItemIds : []),
    [filteredItemIds]
  );

  // Debounced preview fetch — category / selected-only toggles no longer thrash the API.
  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const cats = Array.from(categories);
        const body = {
          estimate_number: estimateNumber,
          job_number: jobNumber,
          rev: selectedRev,
          categories: cats.length ? cats : ['valves'],
        };
        if (selectedOnly && selectedIdsArr.length > 0) body.ids = selectedIdsArr;
        else if (cats.includes('custom')) body.ids = filteredIdsArr;
        const resp = await authenticatedApiCall(instance, accounts, 'POST', '/mto/purchasing-preview', body);
        if (!cancelled && resp.data?.success) {
          const lines = resp.data.data || [];
          setPreview(lines);
          setNextReq(resp.data.next_requisition_no || '');
          setQtyEdits({});
          setCheckedKeys(new Set(lines.map((l) => purchasingLineKey(l))));
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('Purchasing preview failed:', err);
          setPreview([]);
        }
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isOpen, categories, selectedOnly, selectedIdsArr, filteredIdsArr, instance, accounts, estimateNumber, jobNumber, selectedRev]);

  // Reset ephemeral fields when the modal closes so next open starts clean.
  useEffect(() => {
    if (isOpen) return;
    setMessage('');
    setSpecFile(null);
    setSpecLink('');
    setQtyEdits({});
    setCheckedKeys(new Set());
    setSelectedOnly(false);
    setPreview([]);
  }, [isOpen]);

  const handleSend = useCallback(async () => {
    const recipientList = recipients.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    const cats = Array.from(categories);
    if (cats.length === 0) {
      showToast('Check at least one What-to-send category', 'error');
      return;
    }
    const selectedKeys = preview.map(purchasingLineKey).filter((k) => checkedKeys.has(k));
    if (selectedKeys.length === 0) {
      showToast('Check at least one line item in the preview', 'error');
      return;
    }
    setSending(true);
    try {
      const payload = {
        estimate_number: estimateNumber,
        job_number: jobNumber,
        rev: selectedRev,
        message: message || undefined,
        categories: cats,
        selected_line_keys: selectedKeys,
        ...header,
      };
      if (recipientList.length) payload.recipients = recipientList;
      if (selectedOnly && selectedIdsArr.length > 0) payload.ids = selectedIdsArr;
      else if (cats.includes('custom')) payload.ids = filteredIdsArr;
      if (specLink.trim()) payload.spec_link = specLink.trim();
      const overrides = Object.entries(qtyEdits)
        .map(([key, val]) => {
          if (!checkedKeys.has(key)) return null;
          const line = preview.find((l) => purchasingLineKey(l) === key);
          const qty = parseFloat(val);
          return line && Number.isFinite(qty) ? { ids: line.mto_ids, purchasing_qty: qty } : null;
        })
        .filter(Boolean);
      if (overrides.length) payload.line_overrides = overrides;

      const fd = new FormData();
      fd.append('payload', JSON.stringify(payload));
      if (specFile) fd.append('spec_file', specFile);
      const resp = await authenticatedApiCall(instance, accounts, 'POST', '/mto/send-to-purchasing', fd);
      if (resp.data?.success) {
        showToast(resp.data.message || 'Sent to purchasing', 'success');
        onClose();
        if (overrides.length && onSent) onSent();
      } else {
        showToast(resp.data?.message || 'Failed to send', 'error');
      }
    } catch (err) {
      showToast(err?.response?.data?.message || 'Failed to send to purchasing', 'error');
    } finally {
      setSending(false);
    }
  }, [
    recipients, categories, preview, checkedKeys, estimateNumber, jobNumber, selectedRev,
    message, header, selectedOnly, selectedIdsArr, filteredIdsArr, specLink, qtyEdits,
    specFile, instance, accounts, showToast, onClose, onSent,
  ]);

  if (!isOpen) return null;

  const allCategoriesOn = CATEGORY_IDS.every((id) => categories.has(id));
  const allPreviewOn = preview.length > 0 && preview.every((l) => checkedKeys.has(purchasingLineKey(l)));

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'white', borderRadius: '10px', maxWidth: '760px', width: '100%', boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }}
      >
        <div style={{
          background: 'linear-gradient(135deg, #059669 0%, #047857 100%)', color: 'white',
          padding: '0.9rem 1.25rem', display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', borderRadius: '10px 10px 0 0',
        }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem' }}>🛒 Send MTO to Purchasing</h3>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'white', fontSize: '1.4rem', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: '1.25rem', fontSize: '0.85rem', color: '#374151', maxHeight: '80vh', overflowY: 'auto' }}>
          <p style={{ margin: '0 0 0.9rem' }}>
            Emails purchase-requisition Excel file(s) to purchasing — <strong>one .xlsx per checked What-to-send category</strong>.
            Check categories below, then refine with line checkboxes. <strong>UNIT</strong> = MTO unit of measure.
            Blank <strong>PRICE</strong> for suppliers. When prices come back, re-import via Part Rate DB <strong>💲 Import Cost</strong>.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.68rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: 2 }}>Requisition No.</label>
              <input
                value={nextReq ? `${nextReq} (auto)` : `Auto: ${estimateNumber || jobNumber || ''}-####`}
                readOnly disabled
                title="The requisition number is generated automatically: estimate number + 4-digit sequence starting at 0001"
                style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#334155', fontWeight: 600, boxSizing: 'border-box', fontSize: '0.8rem' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.68rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: 2 }}>Request Type</label>
              <select
                value={header.request_type}
                onChange={(e) => setHeader((h) => ({ ...h, request_type: e.target.value }))}
                style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1', boxSizing: 'border-box', fontSize: '0.8rem' }}
              >
                <option value="Quote">Quote</option>
                <option value="Purchase">Purchase</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.68rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: 2 }}>Ship To</label>
              <input value={header.ship_to} onChange={(e) => setHeader((h) => ({ ...h, ship_to: e.target.value }))} placeholder="Site / warehouse" style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1', boxSizing: 'border-box', fontSize: '0.8rem' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.68rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: 2 }}>Date Required</label>
              <input type="date" value={header.date_required} onChange={(e) => setHeader((h) => ({ ...h, date_required: e.target.value }))} style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1', boxSizing: 'border-box', fontSize: '0.8rem' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.68rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: 2 }}>Site Contact</label>
              <input value={header.site_contact} onChange={(e) => setHeader((h) => ({ ...h, site_contact: e.target.value }))} placeholder="Name" style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1', boxSizing: 'border-box', fontSize: '0.8rem' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.68rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: 2 }}>Contact No.</label>
              <input value={header.contact_no} onChange={(e) => setHeader((h) => ({ ...h, contact_no: e.target.value }))} placeholder="Phone" style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1', boxSizing: 'border-box', fontSize: '0.8rem' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.68rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: 2 }}>Spec Class</label>
              <input value={header.spec_class} onChange={(e) => setHeader((h) => ({ ...h, spec_class: e.target.value }))} placeholder="e.g. Vic-Coupling" style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1', boxSizing: 'border-box', fontSize: '0.8rem' }} />
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <label style={{ fontSize: '0.68rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
                What to send ({categories.size} → {categories.size} Excel file{categories.size === 1 ? '' : 's'})
              </label>
              <button
                type="button"
                onClick={() => setCategories(allCategoriesOn ? new Set() : new Set(CATEGORY_IDS))}
                style={{ background: 'none', border: 'none', color: '#059669', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600 }}
              >
                {allCategoriesOn ? 'Uncheck categories' : 'Check all categories'}
              </button>
            </div>
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '4px 10px',
              border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px', background: '#f8fafc',
            }}>
              {[
                { id: 'valves', label: 'Valves' },
                { id: 'ubolts', label: 'U-bolts' },
                { id: 'gaskets', label: 'Gaskets' },
                { id: 'cs', label: 'CS' },
                { id: 'ss_duplex', label: 'SS & Duplex' },
                { id: 'hdpe', label: 'HDPE' },
                { id: 'cs_vic', label: 'CS & Vic' },
                { id: 'cpvc', label: 'CPVC' },
                { id: 'custom', label: `Filtered only (${filteredIdsArr.length})` },
              ].map((opt) => (
                <label key={opt.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: '#334155', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={categories.has(opt.id)}
                    onChange={(e) => {
                      setCategories((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(opt.id);
                        else next.delete(opt.id);
                        return next;
                      });
                    }}
                    style={{ accentColor: '#059669' }}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
            <p style={{ margin: '4px 0 0', fontSize: '0.7rem', color: '#94a3b8' }}>
              Each checked category is emailed as its own .xlsx (e.g. Valves.xlsx, CS.xlsx).
            </p>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: '0.68rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: 2 }}>Spec link (SharePoint / Azure, optional)</label>
            <input value={specLink} onChange={(e) => setSpecLink(e.target.value)} placeholder="https://…" style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1', boxSizing: 'border-box', fontSize: '0.8rem' }} />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: '0.68rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: 2 }}>Attach spec document (optional, max 25 MB)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="file" onChange={(e) => setSpecFile(e.target.files?.[0] || null)} style={{ fontSize: '0.78rem' }} />
              {specFile && (
                <button onClick={() => setSpecFile(null)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '0.75rem' }}>✕ remove</button>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
            <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
              Items to send {previewLoading ? '(loading…)' : `(${checkedKeys.size}/${preview.length} selected)`}
            </label>
            {preview.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  const allKeys = preview.map(purchasingLineKey);
                  setCheckedKeys(allPreviewOn ? new Set() : new Set(allKeys));
                }}
                style={{ background: 'none', border: 'none', color: '#059669', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600 }}
              >
                {allPreviewOn ? 'Uncheck all' : 'Check all'}
              </button>
            )}
          </div>
          <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, maxHeight: 220, overflowY: 'auto', marginBottom: 4 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
              <thead>
                <tr style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
                  <th style={{ textAlign: 'center', padding: '5px 6px', borderBottom: '1px solid #e2e8f0', fontWeight: 600, color: '#64748b', width: 36 }} />
                  <th style={{ textAlign: 'left', padding: '5px 8px', borderBottom: '1px solid #e2e8f0', fontWeight: 600, color: '#64748b' }}>Description</th>
                  <th style={{ textAlign: 'right', padding: '5px 8px', borderBottom: '1px solid #e2e8f0', fontWeight: 600, color: '#64748b', width: 60 }}>Qty</th>
                  <th style={{ textAlign: 'right', padding: '5px 8px', borderBottom: '1px solid #e2e8f0', fontWeight: 600, color: '#64748b', width: 100 }}>Purchasing Qty</th>
                  <th style={{ textAlign: 'left', padding: '5px 8px', borderBottom: '1px solid #e2e8f0', fontWeight: 600, color: '#64748b', width: 50 }}>Unit</th>
                </tr>
              </thead>
              <tbody>
                {preview.length === 0 && !previewLoading && (
                  <tr><td colSpan={5} style={{ padding: '10px 8px', color: '#94a3b8', textAlign: 'center' }}>No items match this selection.</td></tr>
                )}
                {preview.map((line) => {
                  const key = purchasingLineKey(line);
                  const edited = qtyEdits[key];
                  const checked = checkedKeys.has(key);
                  return (
                    <tr key={key} style={{ background: checked ? 'transparent' : '#f8fafc', opacity: checked ? 1 : 0.65 }}>
                      <td style={{ padding: '4px 6px', borderBottom: '1px solid #f1f5f9', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            setCheckedKeys((prev) => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(key);
                              else next.delete(key);
                              return next;
                            });
                          }}
                          style={{ accentColor: '#059669', cursor: 'pointer' }}
                          title="Include this item in the send"
                        />
                      </td>
                      <td style={{ padding: '4px 8px', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 360 }} title={line.description}>{line.description}</td>
                      <td style={{ padding: '4px 8px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', color: '#64748b' }}>{Number(line.qty).toLocaleString()}</td>
                      <td style={{ padding: '4px 8px', borderBottom: '1px solid #f1f5f9', textAlign: 'right' }}>
                        <input
                          type="number" min="0" step="any"
                          value={edited !== undefined ? edited : (Number(line.purchasing_qty) || 0)}
                          onChange={(e) => setQtyEdits((prev) => ({ ...prev, [key]: e.target.value }))}
                          title="Quantity to purchase incl. waste allowance — written back to the MTO rows on send"
                          style={{ width: 80, padding: '3px 6px', borderRadius: 4, border: edited !== undefined ? '1px solid #059669' : '1px solid #cbd5e1', textAlign: 'right', fontSize: '0.75rem' }}
                        />
                      </td>
                      <td style={{ padding: '4px 8px', borderBottom: '1px solid #f1f5f9', color: '#64748b' }}>{line.unit || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p style={{ margin: '0 0 0.9rem', fontSize: '0.72rem', color: '#94a3b8' }}>
            Line checkboxes choose which rows go into the category Excel files. UNIT from MTO UM (Pipe→FT / else EA when blank). Purchasing Qty edits save back to MTO on send.
          </p>

          <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: 3 }}>Recipients (comma-separated emails)</label>
          <input value={recipients} onChange={(e) => setRecipients(e.target.value)} placeholder="sarah.s@laframboise.net, steph.marleau@laframboise.net" style={{ width: '100%', padding: '7px 9px', borderRadius: 6, border: '1px solid #cbd5e1', boxSizing: 'border-box', fontSize: '0.82rem', marginBottom: 4 }} />
          <p style={{ margin: '0 0 0.9rem', fontSize: '0.72rem', color: '#94a3b8' }}>Leave blank to use the configured purchasing distribution list.</p>
          <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: 3 }}>Message (optional)</label>
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} placeholder="Any notes for the purchasing team…" style={{ width: '100%', padding: '7px 9px', borderRadius: 6, border: '1px solid #cbd5e1', boxSizing: 'border-box', fontSize: '0.82rem', marginBottom: 10, resize: 'vertical' }} />
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.82rem', marginBottom: 4, cursor: selectedIdsArr.length > 0 ? 'pointer' : 'not-allowed', opacity: selectedIdsArr.length > 0 ? 1 : 0.5 }}>
            <input type="checkbox" disabled={selectedIdsArr.length === 0} checked={selectedOnly} onChange={(e) => setSelectedOnly(e.target.checked)} style={{ accentColor: '#059669' }} />
            Only send selected rows{selectedIdsArr.length > 0 ? ` (${selectedIdsArr.length})` : ''}
          </label>
          <p style={{ margin: '0 0 1rem', fontSize: '0.72rem', color: '#94a3b8' }}>
            Otherwise all rows for {selectedRev === 'all' ? 'all revisions' : `revision ${selectedRev}`} of {estimateNumber || jobNumber} are sent.
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button onClick={onClose} style={{ ...BTN }}>Cancel</button>
            <button
              onClick={handleSend}
              disabled={sending}
              style={{ ...BTN, backgroundColor: '#059669', color: 'white', borderColor: '#059669', cursor: sending ? 'not-allowed' : 'pointer' }}
            >
              {sending ? 'Sending…' : '📤 Send'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

export default SendToPurchasingModal;
